import Drawer from 'devextreme-react/drawer';
import ScrollView from 'devextreme-react/scroll-view';
import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Footer, Header, SideNavigationMenu } from '../../components';
import './side-nav-inner-toolbar.scss';
import { useScreenSize } from '../../utils/media-query';
import { Template } from 'devextreme-react/core/template';
import { useMenuPatch } from '../../utils/patches';
import { ItemClickEvent } from 'devextreme/ui/tree_view';
import type { SideNavToolbarProps } from '../../types';
import { ClickEvent } from 'devextreme/ui/button';
import { AnnouncementAlert } from '../../features/common';

export default function SideNavInnerToolbar({ title, children }: React.PropsWithChildren<SideNavToolbarProps>) {
	const scrollViewRef = useRef<ScrollView>(null);
	const navigate = useNavigate();
	const { isTiny, isXSmall, isSmall, isMedium, isLarge } = useScreenSize();
	const isNarrowScreen = isTiny || isXSmall || isSmall;
	const isWideScreen = isMedium || isLarge;
	const [patchCssClass, onMenuReady] = useMenuPatch();
	const [menuStatus, setMenuStatus] = useState(
		isWideScreen ? MenuStatus.Opened : MenuStatus.Closed
	);

	const toggleMenu = useCallback(({ event }: ClickEvent) => {
		setMenuStatus(
			prevMenuStatus => prevMenuStatus === MenuStatus.Closed
				? MenuStatus.Opened
				: MenuStatus.Closed
		);
		event?.stopPropagation();
	}, []);

	const temporaryOpenMenu = useCallback(() => {
		setMenuStatus(
			prevMenuStatus => prevMenuStatus === MenuStatus.Closed
				? MenuStatus.TemporaryOpened
				: prevMenuStatus
		);
	}, []);

	const onOutsideClick = useCallback(() => {
		setMenuStatus(
			prevMenuStatus => prevMenuStatus !== MenuStatus.Closed && !isWideScreen
				? MenuStatus.Closed
				: prevMenuStatus
		);
		return menuStatus === MenuStatus.Closed ? true : false;
	}, [isWideScreen, menuStatus]);

	const onNavigationChanged = useCallback(({ itemData, component, event, node }: ItemClickEvent) => {
		if (menuStatus === MenuStatus.Closed) {
			event?.preventDefault();
			return;
		}

		if (itemData?.path) {
			navigate(itemData.path);
		}
		else if (!itemData?.onClick) {
			if (itemData?.items && itemData?.items.length > 0) {
				component.expandItem(itemData);
				navigate(itemData?.items[0].path);
			}
		}

		scrollViewRef.current?.instance.scrollTo(0);

		if (!isWideScreen || menuStatus === MenuStatus.TemporaryOpened) {
			setMenuStatus(MenuStatus.Closed);
			event?.stopPropagation();
		}
	}, [navigate, menuStatus, isWideScreen]);

	const [hasAnnouncement, setHasAnnouncement] = useState(false);

	return (
		<div className={'side-nav-inner-toolbar' + (hasAnnouncement ? ' has-announcement' : '')}>
			<AnnouncementAlert onVisibilityChanged={(visible) => { setHasAnnouncement(visible); }} />
			<Header
				toggleMenu={toggleMenu}
				showToggleMenuButton={!isWideScreen}
			/>
			<Drawer
				className={['drawer', patchCssClass].join(' ')}
				position={'before'}
				closeOnOutsideClick={onOutsideClick}
				openedStateMode={isWideScreen ? 'shrink' : 'overlap'}
				revealMode={isNarrowScreen ? 'slide' : 'expand'}
				minSize={isNarrowScreen ? 0 : 15}
				maxSize={310}
				shading={isWideScreen ? false : true}
				opened={isNarrowScreen || menuStatus === MenuStatus.Closed ? false : true}
				template={'menu'}
			>
				<div className={'container'}>
					<ScrollView ref={scrollViewRef} className={'layout-body with-footer'}>
						<div className={'content'}>
							{React.Children.map(children, (item: any) => {
								return item.type !== Footer && item;
							})}
						</div>
						<div className={'content-block'}>
							{React.Children.map(children, (item: any) => {
								return item.type === Footer && item;
							})}
						</div>
					</ScrollView>
				</div>
				<Template name={'menu'}>
					<SideNavigationMenu
						compactMode={menuStatus === MenuStatus.Closed}
						selectedItemChanged={onNavigationChanged}
						openMenu={temporaryOpenMenu}
						onMenuReady={onMenuReady}
						toggleMenu={toggleMenu}
						showToggleMenuButton={isWideScreen}
					>
					</SideNavigationMenu>
				</Template>
			</Drawer>
		</div>
	);
}

const MenuStatus = {
	Closed: 1,
	Opened: 2,
	TemporaryOpened: 3
};

